<template>
  <LoadingSpinner v-if="isLoading" />

  <div v-else-if="!isError">
    <div
      v-if="project"
      :key="project.id"
      class="view-project"
    >
      <ProjectHeader :project="project" />
      <ProjectSubNavigation :project="project" />

      <EmptyState v-if="isErrorLineItems">
        <template #icon>
          <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
        </template>
        <template #title>
          <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
        </template>
        <template #description>
          <p class="text-sm text-center text-slate-500 pt-4">
            An error occurred while trying to load the budget line items
          </p>
        </template>
      </EmptyState>

      <div
        v-else
        class="mt-6"
      >
        <!-- Show Mass Update Modal Btn -->
        <div class="flex justify-end gap-2">
          <!-- select all btn -->
          <BaseButton
            variant="base"
            :text="selectedLineItems.length > 0 ? 'Unselect All' : 'Select All'"
            @click="selectOrUnselectAll"
          />

          <!-- mass update btn -->
          <BaseButton
            variant="base"
            :text="`Mass Update ${selectedLineItems.length > 0 ? '('+ selectedLineItems.length +')' : ''}`"
            :disabled="selectedLineItems.length <= 1"
            :class="{ 'cursor-not-allowed opacity-50': selectedLineItems.length <= 1 }"
            @click="showMassUploadTaskModal"
          />
        </div>

        <!-- Board -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 mt-6">
          <div
            v-for="(column) in columns"
            :key="column.name"
            class="bg-slate-100 px-2 pt-4 rounded-md md:h-[calc(100vh-20rem)] overflow-hidden"
          >
            <h3 class="text-lg font-semibold leading-6 px-2">{{ column.name }} ({{ column.tasks.length }})</h3>

            <draggable
              :list="column.tasks"
              item-key="id"
              v-bind="dragOptions"
              class="mt-4 space-y-4 transition-all overflow-y-auto px-2 h-[84%] pb-4"
              :data-column-name="column.name"
              @change="(evt) => onTaskAdd(evt, column.name)"
            >
              <template #item="{ element }">
                <div class="bg-white shadow-md rounded-md p-4">
                  <div class="flex items-center mb-2 gap-2">
                    <div class="!-mb-4">
                      <BaseCheckbox
                        v-model="selectedLineItems"
                        :value="element.id"
                      />
                    </div>

                    <h4
                      class="text-lg font-semibold truncate cursor-pointer"
                      @click="showTaskDetails(element.id)"
                    >
                      {{ element.title }}
                    </h4>
                  </div>

                  <p class="text-sm text-slate-500"><span class="inline-block mr-3">Status: </span><LineItemBadgeStatus :status="element.status" /></p>
                  <p class="text-sm text-slate-500 mt-3"><span v-if="element.assignedTo">Assigned To: {{ getAssignee(element.assignedTo) }}</span> <span v-else>Unassigned</span></p>

                </div>
              </template>
            </draggable>
          </div>
        </div>


      </div>
    </div>
  </div>

  <EmptyState v-else>
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        An error occurred while trying to load the project
      </p>
    </template>
  </EmptyState>


  <Modal
    id="update-task-details"
    v-model="showModal"
    :title="modalTitle"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="w-full md:w-[80%] lg:w-[70%] xl:w-[50%]"
  >
    <template #body>
      <div class="px-4 py-6">
        <div class="grid grid-cols-2 gap-4">
          <!-- start date -->
          <BaseInputDate
            v-model="selectedLineItem.startDate"
            label="Start Date"
          />

          <!-- end date -->
          <BaseInputDate
            v-model="selectedLineItem.endDate"
            label="End Date"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <!-- Depends On -->
           <BaseAutoComplete
              v-model="dependsOn"
              label="Depends On"
              :options="dependsOnOptions"
              :placeholder="'Select a task'"
            />

          <!-- Assign Sub -->
          <SubsAutoComplete
            v-model="selectedLineItem.assignedTo"
            label="Assign Sub"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <!-- Status -->
          <BaseSelect
            v-model="selectedLineItem.status"
            label="Status"
            :options="statusOptions"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="bg-white py-3 px-6 flex justify-end gap-2 mt-4">
        <BaseButton
          variant="base"
          text="Cancel"
          :disabled="isSavingLineItem"
          :class="{ 'cursor-not-allowed opacity-50': isSavingLineItem }"
          @click="showModal = false"
        />

        <BaseButton
          variant="primary"
          :disabled="isSavingLineItem"
          :class="{ 'cursor-not-allowed opacity-50': isSavingLineItem }"
          @click="saveLineItem(selectedLineItem)"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Save</span>
            <span
              v-if="isSavingLineItem"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>

    </template>
  </Modal>

  <Modal
    id="mass-update-task"
    v-model="showMassUpdateModal"
    :title="massUpdateTitle"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="w-full md:w-[80%] lg:w-[70%] xl:w-[50%]"
  >
    <template #body>
      <div class="px-4 py-6">
        <div class="grid grid-cols-2 gap-4">
          <!-- start date -->
          <BaseInputDate
            v-model="massUpdateDetails.startDate"
            label="Start Date"
          />

          <!-- end date -->
          <BaseInputDate
            v-model="massUpdateDetails.endDate"
            label="End Date"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <!-- Depends On -->
          <BaseAutoComplete
            v-model="massUpdateDependsOn"
            label="Depends On"
            :options="massUpdateDependsOnOptions"
            :placeholder="'Select a task'"
          />

          <!-- Assign Sub -->
          <SubsAutoComplete
            v-model="massUpdateDetails.assignedTo"
            label="Assign Sub"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 mt-4">
          <!-- Status -->
          <BaseSelect
            v-model="massUpdateDetails.status"
            label="Status"
            :options="statusOptions"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="bg-white py-3 px-6 flex justify-end gap-2 mt-4">
        <BaseButton
          variant="base"
          text="Cancel"
          :disabled="isSavingLineItem"
          :class="{ 'cursor-not-allowed opacity-50': isSavingLineItem }"
          @click="showMassUpdateModal = false"
        />

        <BaseButton
          variant="primary"
          :disabled="isSavingLineItem"
          :class="{ 'cursor-not-allowed opacity-50': isSavingLineItem }"
          @click="massUpdateLineItems"
        >
          <div class="flex justify-between items-center gap-2">
            <span>Save</span>
            <span
              v-if="isSavingLineItem"
              class="i-heroicons-arrow-path animate-spin"
            />
          </div>
        </BaseButton>
      </div>

    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import draggable from 'vuedraggable'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import EmptyState from '@/components/app/EmptyState.vue'
import { useRoute } from 'vue-router'
import { useMutation, useQuery } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import BudgetService from '@/services/BudgetService'
import { BUDGET_ITEM_STATUS, BudgetItem } from '@/models/budget'
import { useNotification } from '@kyvg/vue3-notification'
import { useHead } from '@unhead/vue'
import { find } from 'underscore'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import Modal from '@/components/app/AppModal.vue'
import BaseInputDate from '@/components/base/BaseInputDate.vue'
import BaseAutoComplete from '@/components/base/BaseAutoComplete.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import LineItemBadgeStatus from '@/components/base/LineItemBadgeStatus.vue'
import ProposalService from '@/services/ProposalService'
import { Proposal, PROPOSAL_STATUS } from '@/models/proposal'
import SubsAutoComplete from '@/components/base/SubsAutoComplete.vue'
import SubcontractorService from '@/services/SubcontractorService'

useHead({ title: 'Project Schedule' })

const $route = useRoute()
const projectId = $route.params.projectId as string
const projectService = new ProjectService()
const budgetService = new BudgetService()
const proposalService = new ProposalService()
const { notify } = useNotification()

const dragOptions = computed(() => ({
  animation: 200,
  group: 'tasks',
  ghostClass: 'ghost',
  dragClass: 'drag',
  chosenClass: 'chosen',
  moveClass: 'move',
  filter: '.ignore-elements',
}))

const { data: project, isLoading: projectLoading, error: projectError } = useQuery({
  queryKey: ['project', projectId],
  queryFn: () => projectService.find(projectId),
  enabled: !!projectId
})

const { data: proposal, isLoading: proposalLoading } = useQuery({
  queryKey: ['proposal', projectId],
  queryFn: () => proposalService.find(projectId),
  enabled: !!projectId
})

const { data: changeOrders, isLoading: changeOrdersLoading } = useQuery({
  queryKey: ['changeOrders', projectId],
  queryFn: async () => await proposalService.getChangeOrders(projectId) as Proposal[],
  enabled: !!projectId
})

const { mutateAsync: updateLineItem } = useMutation({
  mutationFn: async (payload: {lineItem: BudgetItem; showNotif: boolean}) => {
    const updatedLineItem = await budgetService.updateLineItem(projectId, payload.lineItem.id, payload.lineItem)
    return { ...updatedLineItem, showNotif: payload.showNotif }
  },
  onSuccess: ({showNotif}) => {
    if (showNotif) {
      notify({
        title: 'Success',
        text: 'Task updated successfully',
        type: 'success'
      })
    }
  },
  onError: (error) => {
    notify({
      title: 'Error',
      text: `Failed to update task: ${error.message}`,
      type: 'error'
    })
    rebuildColumns()
  }
})


const isLoading = computed(() => projectLoading.value || proposalLoading.value || changeOrdersLoading.value)
const isError = computed(() => projectError.value)

const lineItems = ref<BudgetItem[]>([])
const isErrorLineItems = ref(false)

interface Task {
  id: string
  title: string
  status: string
  assignedTo: string
}

interface Column {
  name: string
  tasks: Task[]
}

const columns = ref<Column[]>([
  { name: 'Tasks', tasks: [] },
  { name: 'In Progress', tasks: [] },
  { name: 'Completed', tasks: [] }
])

const statusOptions = computed(() => [
  { value: BUDGET_ITEM_STATUS.ACCEPTED, label: 'Accepted' },
  { value: BUDGET_ITEM_STATUS.SCHEDULED, label: 'Scheduled' },
  { value: BUDGET_ITEM_STATUS.IN_PROGRESS, label: 'In Progress' },
  { value: BUDGET_ITEM_STATUS.ON_HOLD, label: 'On Hold' },
  { value: BUDGET_ITEM_STATUS.COMPLETED, label: 'Completed' },
])

const rebuildColumns = () => {
  columns.value = [
    {
      name: 'Tasks',
      tasks: lineItems.value
        .filter(item => !item.status || item.status === BUDGET_ITEM_STATUS.ON_HOLD || item.status === BUDGET_ITEM_STATUS.ACCEPTED)
        .map((item) => toTask(item, !item.status ? BUDGET_ITEM_STATUS.ON_HOLD : item.status))
    },
    {
      name: 'In Progress',
      tasks: lineItems.value
        .filter(item => [BUDGET_ITEM_STATUS.IN_PROGRESS, BUDGET_ITEM_STATUS.SCHEDULED].includes(item.status))
        .map((item) => toTask(item, item.status))
    },
    {
      name: 'Completed',
      tasks: lineItems.value
        .filter(item => item.status === BUDGET_ITEM_STATUS.COMPLETED)
        .map((item) => toTask(item, item.status))
    }
  ]
}

// Fetch line items when project is loaded
watch(isLoading, async (value) => {
  if (value) return

  if (project.value && proposal.value) {
    try {
      lineItems.value = await budgetService.getLineItems(project.value.id) as BudgetItem[]

      const allLineItemsIDs = [...proposal.value.lineItems]

      // get line items from accepted change orders
      if (changeOrders.value) {
        changeOrders.value.forEach((changeOrder) => {
          if (changeOrder.status === PROPOSAL_STATUS.ACCEPTED) {
            allLineItemsIDs.push(...changeOrder.lineItems)
          }
        })
      }

      // filter line items that are not selected and credit line items
      lineItems.value = lineItems.value.filter((lineItem) => {
        return allLineItemsIDs.some((id) => id === lineItem.id) && lineItem.selected && !lineItem.isCreditLineItem
      })

      rebuildColumns()

    } catch (error) {
      console.log('Error fetching line items:', error)
      isErrorLineItems.value = true
    }
  }
}, { immediate: true })

// convert line item to task for the board
function toTask(item: BudgetItem, status: string): Task {
  const colItem =  {
    id: item.id,
    title: item.title,
    status: status,
    assignedTo: item.assignedTo
  }
  return colItem
}

// update line item when task is moved to another column
const onTaskAdd = async (evt: any, columnName: string) => {
  if (!evt.added) return

  const movedTask: Task = evt.added.element

  if (!movedTask) return

  const index = lineItems.value.findIndex(i => i.id === movedTask.id)
  const item = lineItems.value[index]

  if (!item) return

  switch (columnName) {
    case 'Tasks':
      item.status = BUDGET_ITEM_STATUS.ON_HOLD
      // reset other fields to default
      item.assignedTo = ''
      item.startDate = null
      item.endDate = null
      item.dependsOn = ''
      break
    case 'In Progress':
      item.status = BUDGET_ITEM_STATUS.IN_PROGRESS
      break
    case 'Completed':
      item.status = BUDGET_ITEM_STATUS.COMPLETED
      break
  }

  lineItems.value = lineItems.value.filter(i => i.id !== movedTask.id)
  lineItems.value.push(item)

  rebuildColumns()
  updateLineItem({lineItem:item, showNotif: true})
}

const dependsOnOptions = computed(() => {
  return lineItems.value.filter(item => item.id !== selectedLineItem.value.id).map(item => ({
    label: item.title,
    value: item.id
  }))
})

const dependsOn = computed({
  get: () => {
    if (dependsOnOptions.value) {
      return find(dependsOnOptions.value, (item) => item.value === selectedLineItem.value.dependsOn)
    }
    return null
  },
  set: (value) => {
    if (selectedLineItem.value) {
      selectedLineItem.value.dependsOn = value.value
    }
  }
})

const showModal = ref(false)
const selectedLineItem = ref<BudgetItem | null>(null)
const modalTitle = ref('Update Task Details')

// show task details modal
const showTaskDetails = (id: string) => {
  const lineItem = lineItems.value.find(item => item.id === id)

  if (!lineItem) {
    notify({
      title: 'Error',
      text: 'Line item not found',
      type: 'error'
    })
    return
  }

  selectedLineItem.value = lineItem
  modalTitle.value = lineItem.title
  showModal.value = true
}

const subService = new SubcontractorService()

const { data: subs } = useQuery({
  queryKey: ['subs'],
  queryFn: () => subService.getAllSubs()
})

// get assignee name from sub id
const getAssignee = (id: string) => {
  console.log('subs', subs.value)
  console.log('id', id)
  if (!subs.value || !id) return 'Unassigned'

  const sub = subs.value.find((sub) => sub.id === id)
  if (sub) {
    return sub.name
  }
  return 'Unassigned'
}

// save line item when modal is closed
const isSavingLineItem = ref(false)
const saveLineItem = async (lineItem: BudgetItem) => {
  isSavingLineItem.value = true
  if (!lineItem) return

  const index = lineItems.value.findIndex(i => i.id === lineItem.id)
  if (index !== -1) {
    lineItems.value[index] = lineItem
  }

  await updateLineItem({lineItem, showNotif: true})
  isSavingLineItem.value = false
  showModal.value = false
  rebuildColumns()
}


const selectedLineItems = ref<string[]>([])

const selectOrUnselectAll = () => {
  if (selectedLineItems.value.length === 0) {
    selectedLineItems.value = lineItems.value.map(item => item.id)
  } else {
    selectedLineItems.value = []
  }
}

const showMassUpdateModal = ref(false)
const massUpdateTitle = ref('Mass Update Task Details')

const massUpdateDetails = ref<BudgetItem | null>({
  ... new BudgetItem(),
  startDate: null,
  endDate: null,
  assignedTo: '',
  status: BUDGET_ITEM_STATUS.ACCEPTED,
  dependsOn: ''
})

const massUpdateDependsOnOptions = computed(() => {
  return lineItems.value.filter(item => !selectedLineItems.value.includes(item.id)).map(item => ({
    label: item.title,
    value: item.id
  }))
})

const massUpdateDependsOn = computed({
  get: () => {
    if (massUpdateDependsOnOptions.value) {
      return find(massUpdateDependsOnOptions.value, (item) => item.value === massUpdateDetails.value?.dependsOn)
    }
    return null
  },
  set: (value) => {
    if (massUpdateDetails.value) {
      massUpdateDetails.value.dependsOn = value.value
    }
  }
})

const showMassUploadTaskModal = () => {
  massUpdateTitle.value = `Mass Update ${selectedLineItems.value.length} Tasks`
  showMassUpdateModal.value = true
}

const massUpdateLineItems = async () => {
  isSavingLineItem.value = true
  if (!massUpdateDetails.value) return

  const updatedLineItems = lineItems.value.filter(item => selectedLineItems.value.includes(item.id))

  for (const item of updatedLineItems) {
    if (massUpdateDetails.value.startDate) {
      item.startDate = massUpdateDetails.value.startDate
    }
    if (massUpdateDetails.value.endDate) {
      item.endDate = massUpdateDetails.value.endDate
    }
    if (massUpdateDetails.value.assignedTo) {
      item.assignedTo = massUpdateDetails.value.assignedTo
    }
    if (massUpdateDetails.value.status) {
      item.status = massUpdateDetails.value.status
    }
    if (massUpdateDetails.value.dependsOn) {
      item.dependsOn = massUpdateDetails.value.dependsOn
    }
  }

  await Promise.all(updatedLineItems.map(item => updateLineItem({lineItem:item, showNotif: false})))

  isSavingLineItem.value = false
  showMassUpdateModal.value = false
  selectedLineItems.value = []
  massUpdateDetails.value = {
    ... new BudgetItem(),
    startDate: null,
    endDate: null,
    assignedTo: '',
    status: BUDGET_ITEM_STATUS.ACCEPTED,
    dependsOn: ''
  }
  rebuildColumns()
}

</script>

<style scoped>
.flip-list-move {
  transition: transform 0.3s ease;
}

.ghost {
  opacity: 0.5;
}

</style>
